// src/services/presentationAPI.js
import { 
  collection, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  getDocs, 
  getDoc,
  query, 
  where, 
  orderBy,
  serverTimestamp,
  onSnapshot,
  writeBatch,
  increment
} from 'firebase/firestore';
import { db } from '../config/firebase';

const PRESENTATIONS_COLLECTION = 'docs-presentations';
const SESSIONS_COLLECTION = 'docs-sessions';
const PARTICIPANTS_COLLECTION = 'docs-participants';
const RESPONSES_COLLECTION = 'docs-responses';

// === PRESENTATION MANAGEMENT ===

export const getUserPresentations = async (userId) => {
  try {
    const q = query(
      collection(db, PRESENTATIONS_COLLECTION),
      where('authorId', '==', userId),
      orderBy('updatedAt', 'desc')
    );
    
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error fetching user presentations:', error);
    throw error;
  }
};

export const createPresentation = async (presentationData, userId) => {
  try {
    const newPresentation = {
      title: presentationData.title || 'Untitled Presentation',
      description: presentationData.description || '',
      authorId: userId,
      aspectRatio: presentationData.aspectRatio || '16:9', // New field
      dimensions: presentationData.dimensions || { width: 1920, height: 1080 }, // New field
      slides: presentationData.slides || [{
        id: Date.now().toString(),
        title: 'Welcome',
        slates: [],
        background: { type: 'color', value: '#ffffff' },
        notes: ''
      }],
      settings: {
        allowQuestions: true,
        showNavigation: true,
        enableReactions: true,
        showParticipantCount: true,
        autoAdvance: false,
        autoAdvanceDelay: 5,
        theme: presentationData.theme || 'modern',
        allowAnonymous: true,
        moderateChat: false,
        enablePolls: true,
        enableQuizzes: true,
        showResults: true,
        allowParticipantNames: true,
        enableHandRaise: true,
        enablePrivateMessages: false,
        recordSession: false,
        maxParticipants: 100,
        sessionTimeout: 180,
        ...presentationData.settings
      },
      isPublic: false,
      allowPublicView: false,
      status: 'draft',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    const docRef = await addDoc(collection(db, PRESENTATIONS_COLLECTION), newPresentation);
    return { id: docRef.id, ...newPresentation };
  } catch (error) {
    console.error('Error creating presentation:', error);
    throw error;
  }
};

export const getPresentation = async (presentationId) => {
  try {
    const docRef = doc(db, PRESENTATIONS_COLLECTION, presentationId);
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) {
      throw new Error('Presentation not found');
    }

    return { id: docSnap.id, ...docSnap.data() };
  } catch (error) {
    console.error('Error fetching presentation:', error);
    throw error;
  }
};

export const updatePresentation = async (presentationId, updates) => {
  try {
    const docRef = doc(db, PRESENTATIONS_COLLECTION, presentationId);
    await updateDoc(docRef, {
      ...updates,
      updatedAt: serverTimestamp()
    });
    return true;
  } catch (error) {
    console.error('Error updating presentation:', error);
    throw error;
  }
};

export const deletePresentation = async (presentationId) => {
  try {
    const docRef = doc(db, PRESENTATIONS_COLLECTION, presentationId);
    await deleteDoc(docRef);
    return true;
  } catch (error) {
    console.error('Error deleting presentation:', error);
    throw error;
  }
};

export const savePresentation = async (presentationData) => {
  try {
    if (presentationData.id) {
      // Update existing presentation
      const updates = { ...presentationData };
      delete updates.id; // Remove id from update data
      await updatePresentation(presentationData.id, updates);
      return presentationData;
    } else {
      // Create new presentation
      return await createPresentation(presentationData, presentationData.authorId);
    }
  } catch (error) {
    console.error('Error saving presentation:', error);
    throw error;
  }
};

export const duplicatePresentation = async (presentationId, userId) => {
  try {
    const original = await getPresentation(presentationId);
    const duplicateData = {
      title: `${original.title} (Copy)`,
      description: original.description,
      slides: original.slides,
      settings: original.settings
    };
    
    return await createPresentation(duplicateData, userId);
  } catch (error) {
    console.error('Error duplicating presentation:', error);
    throw error;
  }
};

// === SESSION MANAGEMENT ===

export const generateSessionCode = () => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let result = '';
  for (let i = 0; i < 6; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
};

export const getSessionByCode = async (sessionCode) => {
  try {
    // Validate session code format first
    if (!isValidSessionCode(sessionCode)) {
      throw new Error('Invalid session code format');
    }

    const q = query(
      collection(db, SESSIONS_COLLECTION),
      where('sessionCode', '==', sessionCode.toUpperCase()),
      where('status', '==', 'active')
    );
    
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      console.log(`No active session found for code: ${sessionCode}`);
      throw new Error('Session not found or inactive');
    }
    
    const sessionDoc = querySnapshot.docs[0];
    const sessionData = { id: sessionDoc.id, ...sessionDoc.data() };
    
    console.log('Session found:', sessionData);
    return sessionData;
  } catch (error) {
    console.error('Error fetching session by code:', sessionCode, error);
    throw error;
  }
};

export const createLiveSession = async (presentationId, hostId) => {
  try {
    // Validate inputs
    if (!presentationId || !hostId) {
      throw new Error('Presentation ID and Host ID are required');
    }

    // Check if presentation exists and user has access
    const presentation = await getPresentation(presentationId);
    if (presentation.authorId !== hostId) {
      throw new Error('Unauthorized to create session for this presentation');
    }

    // Generate unique session code
    let sessionCode;
    let attempts = 0;
    do {
      sessionCode = generateSessionCode();
      attempts++;
      
      if (attempts > 10) {
        throw new Error('Failed to generate unique session code');
      }
      
      // Check if code already exists
      const existingSession = await checkSessionCodeExists(sessionCode);
      if (!existingSession) break;
    } while (attempts < 10);
    
    const sessionData = {
      presentationId,
      hostId,
      sessionCode,
      currentSlideIndex: 0,
      status: 'active',
      participantCount: 0,
      maxParticipants: presentation.settings?.maxParticipants || 100,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      // Add presentation metadata for easier access
      presentationTitle: presentation.title,
      presentationAspectRatio: presentation.aspectRatio || '16:9'
    };

    const docRef = await addDoc(collection(db, SESSIONS_COLLECTION), sessionData);
    const createdSession = { id: docRef.id, ...sessionData };
    
    console.log('Session created successfully:', createdSession);
    return createdSession;
  } catch (error) {
    console.error('Error creating live session:', error);
    throw error;
  }
};

// Helper function to check if session code exists
const checkSessionCodeExists = async (sessionCode) => {
  try {
    const q = query(
      collection(db, SESSIONS_COLLECTION),
      where('sessionCode', '==', sessionCode),
      where('status', '==', 'active')
    );
    
    const querySnapshot = await getDocs(q);
    return !querySnapshot.empty;
  } catch (error) {
    console.error('Error checking session code:', error);
    return false;
  }
};

// Enhanced session validation
export const isValidSessionCode = (code) => {
  return code && typeof code === 'string' && /^[A-Z0-9]{6}$/.test(code.toUpperCase());
};

export const formatSessionCode = (code) => {
  if (!code) return '';
  return code.toUpperCase().replace(/[^A-Z0-9]/g, '').substring(0, 6);
};

export const getSession = async (sessionId) => {
  try {
    const docRef = doc(db, SESSIONS_COLLECTION, sessionId);
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) {
      throw new Error('Session not found');
    }

    return { id: docSnap.id, ...docSnap.data() };
  } catch (error) {
    console.error('Error fetching session:', error);
    throw error;
  }
};

export const updateSessionSlide = async (sessionId, slideIndex) => {
  try {
    const sessionRef = doc(db, SESSIONS_COLLECTION, sessionId);
    await updateDoc(sessionRef, {
      currentSlideIndex: slideIndex,
      updatedAt: serverTimestamp()
    });
    return true;
  } catch (error) {
    console.error('Error updating session slide:', error);
    throw error;
  }
};

export const endSession = async (sessionId) => {
  try {
    const sessionRef = doc(db, SESSIONS_COLLECTION, sessionId);
    await updateDoc(sessionRef, {
      status: 'ended',
      endedAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    return true;
  } catch (error) {
    console.error('Error ending session:', error);
    throw error;
  }
};

export const subscribeToSession = (sessionId, callback) => {
  const sessionRef = doc(db, SESSIONS_COLLECTION, sessionId);
  return onSnapshot(sessionRef, callback);
};

// === PARTICIPANT MANAGEMENT ===

export const addParticipant = async (sessionCode, participantData) => {
  try {
    const session = await getSessionByCode(sessionCode);
    
    const participant = {
      sessionId: session.id,
      sessionCode,
      name: participantData.name,
      isAnonymous: participantData.isAnonymous || false,
      joinedAt: serverTimestamp(),
      lastActivity: serverTimestamp(),
      status: 'active'
    };

    const docRef = await addDoc(collection(db, PARTICIPANTS_COLLECTION), participant);
    
    // Update session participant count
    const sessionRef = doc(db, SESSIONS_COLLECTION, session.id);
    await updateDoc(sessionRef, {
      participantCount: increment(1),
      updatedAt: serverTimestamp()
    });

    return { id: docRef.id, ...participant };
  } catch (error) {
    console.error('Error adding participant:', error);
    throw error;
  }
};

export const getSessionParticipants = async (sessionId) => {
  try {
    const q = query(
      collection(db, PARTICIPANTS_COLLECTION),
      where('sessionId', '==', sessionId),
      where('status', '==', 'active')
    );
    
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error fetching session participants:', error);
    throw error;
  }
};

// === INTERACTION MANAGEMENT ===

export const submitInteraction = async (sessionCode, interaction) => {
  try {
    const interactionData = {
      sessionCode,
      participantId: interaction.participantId,
      type: interaction.type,
      data: interaction.data,
      slideIndex: interaction.slideIndex,
      timestamp: serverTimestamp()
    };

    const docRef = await addDoc(collection(db, RESPONSES_COLLECTION), interactionData);
    return { id: docRef.id, ...interactionData };
  } catch (error) {
    console.error('Error submitting interaction:', error);
    throw error;
  }
};

export const getSessionResponses = async (sessionId) => {
  try {
    const q = query(
      collection(db, RESPONSES_COLLECTION),
      where('sessionId', '==', sessionId),
      orderBy('timestamp', 'desc')
    );
    
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error fetching session responses:', error);
    throw error;
  }
};

export const getSessionAnalytics = async (sessionId) => {
  try {
    const responses = await getSessionResponses(sessionId);
    const participants = await getSessionParticipants(sessionId);
    
    return {
      totalResponses: responses.length,
      totalParticipants: participants.length,
      responseRate: participants.length > 0 ? (responses.length / participants.length) * 100 : 0,
      responses,
      participants
    };
  } catch (error) {
    console.error('Error fetching session analytics:', error);
    throw error;
  }
};

// === SHARING AND PERMISSIONS ===

export const sharePresentation = async (presentationId, shareSettings) => {
  try {
    await updatePresentation(presentationId, {
      isPublic: shareSettings.isPublic || false,
      allowPublicView: shareSettings.allowPublicView || false,
      shareSettings: shareSettings
    });
    return true;
  } catch (error) {
    console.error('Error sharing presentation:', error);
    throw error;
  }
};

export const getPublicPresentation = async (presentationId) => {
  try {
    const presentation = await getPresentation(presentationId);
    
    if (!presentation.isPublic && !presentation.allowPublicView) {
      throw new Error('Presentation is not public');
    }
    
    return presentation;
  } catch (error) {
    console.error('Error fetching public presentation:', error);
    throw error;
  }
};